import { useEffect, useRef, useState } from "react";
import { useT } from "@/i18n/LanguageContext";

/**
 * NewsletterSignup — the single reusable StoicOs.ai newsletter component.
 * Embeds the official Kit form (data-uid 2e152846df) exactly once per page:
 * a module-level owner guard means that when several newsletter components
 * exist in the same layout, only the first mounted instance injects the Kit
 * script and renders; the others render nothing. Subscription, confirmation
 * and e-mail handling stay fully inside Kit — no custom storage.
 */

const KIT_UID = "2e152846df";
const KIT_SRC = `https://stoicos-ai-newsletter.kit.com/${KIT_UID}/index.js`;

let activeOwner = null; // page-level single-form guard

const COPY = {
  en: { eyebrow: "StoicOs.ai Newsletter" },
  nl: { eyebrow: "StoicOs.ai Nieuwsbrief" },
};

/**
 * The Kit form itself is authored in English inside Kit. To honour the site
 * language toggle we translate the visible strings of the injected form
 * in-place (same form, same uid, submission untouched) and restore English
 * when the visitor switches back. Exact-match pairs only — nothing else in
 * the injected markup is touched.
 */
const TEXT_PAIRS = [
  ["Become a Traveler of Wisdom", "Word een Reiziger van Wijsheid"],
  ["Join the", "Word lid van de"],
  ["Newsletter and receive practical Stoic wisdom, new Living Library articles, Academy insights, and thoughtful reflections to help you live with greater clarity, courage, justice, and temperance.",
   "Nieuwsbrief en ontvang praktische stoïcijnse wijsheid, nieuwe Living Library-artikelen, Academy-inzichten en doordachte reflecties die je helpen te leven met meer helderheid, moed, rechtvaardigheid en matigheid."],
  ["Email Address", "E-mailadres"],
  ["Join the Journey", "Begin de Reis"],
  ["Weekly practical wisdom. No spam. Unsubscribe at any time.",
   "Wekelijkse praktische wijsheid. Geen spam. Uitschrijven kan altijd."],
  ["Success! Now check your email to confirm your subscription.",
   "Gelukt! Bevestig je inschrijving via de e-mail die we je zojuist hebben gestuurd."],
];

const norm = (t) => (t || "").replace(/\s+/g, " ").trim();

function applyFormLang(root, toNl) {
  const map = new Map(TEXT_PAIRS.map(([en, nl]) => (toNl ? [norm(en), nl] : [norm(nl), en])));
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node;
  while ((node = walker.nextNode())) {
    const key = norm(node.textContent);
    if (map.has(key)) {
      const m = node.textContent.match(/^(\s*)[\s\S]*?(\s*)$/);
      node.textContent = (m ? m[1] : "") + map.get(key) + (m ? m[2] : "");
    }
  }
  root.querySelectorAll("input[placeholder], input[aria-label]").forEach((el) => {
    ["placeholder", "aria-label"].forEach((attr) => {
      const key = norm(el.getAttribute(attr));
      if (key && map.has(key)) el.setAttribute(attr, map.get(key));
    });
  });
}

function ensureLanguageField(root, langValue) {
  const form = root.querySelector("form");
  if (!form) return;
  let input = form.querySelector('input[name="fields[newsletter_language]"]');
  if (!input) {
    input = document.createElement("input");
    input.type = "hidden";
    input.name = "fields[newsletter_language]";
    input.setAttribute("data-testid", "newsletter-language-field");
    form.appendChild(input);
  }
  if (input.value !== langValue) input.value = langValue;
}

export default function NewsletterSignup({ variant = "global", embedded = false }) {
  const { lang } = useT();
  const c = COPY[lang] || COPY.en;
  const holder = useRef(null);
  const idRef = useRef({});
  const [isOwner, setIsOwner] = useState(false);

  useEffect(() => {
    if (activeOwner === null) {
      activeOwner = idRef.current;
      setIsOwner(true);
    }
    return () => {
      if (activeOwner === idRef.current) activeOwner = null;
    };
  }, []);

  useEffect(() => {
    if (!isOwner || !holder.current) return;
    if (holder.current.querySelector(`script[data-uid="${KIT_UID}"]`)) return;
    const s = document.createElement("script");
    s.async = true;
    s.setAttribute("data-uid", KIT_UID);
    s.src = KIT_SRC;
    holder.current.appendChild(s);
  }, [isOwner]);

  // Keep the injected Kit form in the active site language, also when Kit
  // (re)renders parts of the form (async load, success state, validation).
  useEffect(() => {
    if (!isOwner || !holder.current) return;
    const root = holder.current;
    const run = () => {
      applyFormLang(root, lang === "nl");
      ensureLanguageField(root, lang === "nl" ? "nl" : "en");
    };
    run();
    const mo = new MutationObserver(run);
    mo.observe(root, { childList: true, subtree: true, characterData: true });
    return () => mo.disconnect();
  }, [isOwner, lang]);

  if (!isOwner) return null;

  return (
    <section
      data-testid={`newsletter-signup-${variant}`}
      className={embedded
        ? "w-full"
        : variant === "article"
          ? "max-w-3xl mx-auto px-6 md:px-10 mt-20 md:mt-24"
          : "max-w-3xl mx-auto px-6 md:px-10 mt-24 md:mt-32 mb-20 md:mb-24"}
    >
      <div className="relative overflow-hidden border border-gold/40 bg-[#050505]">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-70"
          style={{
            background:
              "radial-gradient(ellipse at 80% 0%, rgba(212,175,55,0.10) 0%, rgba(0,0,0,0) 55%)",
          }}
        />
        <div className="relative p-8 md:p-12">
          <p className="text-[10px] tracking-[0.32em] uppercase text-gold text-center">
            {c.eyebrow}
          </p>
          <div
            ref={holder}
            data-testid="newsletter-kit-container"
            className="mt-6 min-h-[220px] w-full max-w-full overflow-x-hidden"
          />
        </div>
      </div>
    </section>
  );
}
